import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { ArrowLeft, FileDown, Printer, Aperture, Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LanguageSelector } from "@/components/LanguageSelector";

const EASE = [0.85, 0, 0.15, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 26 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function OnePager() {
  const navigate = useNavigate();
  const { t } = useApp();
  const op = t.onepager;
  const paperRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  const exportPdf = async () => {
    const html2pdf = (await import("html2pdf.js")).default;
    const opt = {
      margin: 0,
      filename: `iFotoLife-one-page.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#F5EFE6" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    };
    html2pdf().set(opt).from(paperRef.current).save();
  };

  return (
    <div className="min-h-screen w-full bg-navy-900 py-0 sm:py-10" data-testid="one-pager">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-50 mx-auto mb-6 flex max-w-[820px] items-center justify-between gap-3 bg-navy-900/80 px-4 py-4 backdrop-blur-xl sm:px-0">
        <button
          onClick={() => navigate("/")}
          data-testid="onepager-back"
          className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-cream/80 backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-champagne/40"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> {t.ui.backToMenu}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            data-testid="onepager-print"
            className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium text-cream/80 backdrop-blur-xl transition-all hover:-translate-y-0.5 hover:border-champagne/40"
          >
            <Printer className="h-4 w-4" strokeWidth={1.5} /> {t.ui.print}
          </button>
          <button
            onClick={exportPdf}
            data-testid="onepager-export"
            className="flex items-center gap-2 rounded-full border border-champagne bg-champagne px-4 py-2 text-xs font-semibold text-navy transition-all hover:-translate-y-0.5"
          >
            <FileDown className="h-4 w-4" strokeWidth={1.5} /> {t.ui.exportPdf}
          </button>
          <LanguageSelector />
        </div>
      </div>

      {/* Paper */}
      <div
        ref={paperRef}
        className="mx-auto max-w-[820px] bg-cream text-navy-900 shadow-2xl"
        data-testid="onepager-paper"
      >
        <div className="p-10 sm:p-14">
          {/* Header */}
          <Reveal className="flex items-center justify-between border-b border-navy-900/15 pb-6">
            <div className="flex items-center gap-3">
              <Aperture className="h-7 w-7 text-[#B8923f]" strokeWidth={1.5} />
              <span className="font-serif text-3xl tracking-tight text-navy-900">{op.title}</span>
            </div>
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#B8923f]">{op.eyebrow}</span>
          </Reveal>

          {/* Slogan */}
          <Reveal delay={0.05} className="py-8">
            <p className="font-serif text-3xl italic leading-tight text-navy-900 sm:text-4xl">“{op.slogan}”</p>
            <p className="mt-5 text-[15px] leading-relaxed text-navy-900/75">{op.intro}</p>
          </Reveal>

          {/* Stats */}
          <Reveal delay={0.1} className="grid grid-cols-2 gap-6 border-y border-navy-900/15 py-8 sm:grid-cols-4">
            {op.stats.map((s, i) => (
              <div key={i}>
                <div className="font-serif text-4xl leading-none text-[#B8923f]">{s.value}</div>
                <div className="mt-2 text-xs leading-snug text-navy-900/70">{s.label}</div>
              </div>
            ))}
          </Reveal>

          {/* Problem / Solution */}
          <div className="grid gap-8 py-8 sm:grid-cols-2">
            <Reveal delay={0.1}>
              <h3 className="mb-2 font-serif text-xl text-navy-900">{op.problemTitle}</h3>
              <p className="text-sm leading-relaxed text-navy-900/75">{op.problem}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <h3 className="mb-2 font-serif text-xl text-navy-900">{op.solutionTitle}</h3>
              <p className="text-sm leading-relaxed text-navy-900/75">{op.solution}</p>
            </Reveal>
          </div>

          {/* Arguments */}
          <Reveal delay={0.1} className="border-t border-navy-900/15 py-8">
            <h3 className="mb-4 font-serif text-xl text-navy-900">{op.argsTitle}</h3>
            <ul className="grid gap-3 sm:grid-cols-1">
              {op.args.map((a, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-[#B8923f] text-cream">
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                  <span className="text-sm leading-relaxed text-navy-900/85">{a}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          {/* Audience */}
          <Reveal delay={0.1} className="border-t border-navy-900/15 py-8">
            <h3 className="mb-4 font-serif text-xl text-navy-900">{op.audienceTitle}</h3>
            <div className="flex flex-wrap gap-2.5">
              {op.audience.map((a, i) => (
                <span key={i} className="rounded-full border border-navy-900/20 px-4 py-1.5 text-xs font-medium text-navy-900/80">
                  {a}
                </span>
              ))}
            </div>
          </Reveal>

          {/* CTA */}
          <Reveal delay={0.1} className="rounded-2xl bg-navy-900 p-8 text-cream">
            <h3 className="mb-2 font-serif text-2xl text-champagne">{op.ctaTitle}</h3>
            <p className="text-sm leading-relaxed text-cream/85">{op.cta}</p>
          </Reveal>
        </div>
      </div>

      <div className="no-print h-16" />
    </div>
  );
}
